/**
 * C07 Mascot UI — 右下角固定顯示的陪伴 sprite + 對話氣泡。
 *
 * 使用 emoji 當 sprite 暫代(後續 boss-designer 可換 PNG / SVG)。
 * 用 inline style + 一支 `<style>` 標籤注入動畫 keyframes,避免污染
 * 既有 styles.css(也讓這個元件能單檔抽出測試)。
 *
 * 學習端限定 — 不掛進 src/game/ 任何 scene。
 */
import { MascotController, type MascotFeedback, type MascotState, idleLine } from './mascot';

/** Mascot sprite 對應表 — 暫用 emoji,後續可替換 PNG */
const SPRITE: Record<MascotState, string> = {
  idle: '\u{1F423}',                // 🐣 小蛋
  cheer: '\u{1F424}',                // 🐤 跳出來歡呼的小雞
  encourage: '\u{1F914}',            // 🤔 思考(代表「再想一下」)
  'streak-celebrate': '\u{1F389}',   // 🎉 慶祝
};

/** 狀態色 — 跟既有 quiz feedback 同調 */
const COLOR: Record<MascotState, string> = {
  idle: '#80cbc4',
  cheer: '#ffd54f',
  encourage: '#80cbc4',
  'streak-celebrate': '#ff8a65',
};

const STYLE_ID = 'mascot-ui-style';
const ROOT_CLASS = 'learn-mascot';

function ensureStyleInjected(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .${ROOT_CLASS} {
      position: fixed;
      right: 16px;
      bottom: 16px;
      z-index: 900;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
      pointer-events: none;
      user-select: none;
      font-family: inherit;
      opacity: 1;
      transition: opacity 220ms ease-out;
    }
    /* QA #263 fix #5/#6: hidden state — fade out then display:none so the
       sprite stops occupying space in front of the answer card / keyboard.
       pointer-events already none on the root; display:none belt-and-braces. */
    .${ROOT_CLASS}.is-hidden {
      opacity: 0;
      pointer-events: none;
    }
    .${ROOT_CLASS}.is-gone {
      display: none !important;
    }
    .${ROOT_CLASS}__bubble {
      max-width: 240px;
      padding: 8px 12px;
      background: rgba(33, 33, 33, 0.92);
      color: #fff;
      border-radius: 12px;
      font-size: 14px;
      line-height: 1.35;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.35);
      opacity: 0;
      transform: translateY(6px);
      transition: opacity 180ms ease-out, transform 180ms ease-out;
    }
    .${ROOT_CLASS}__bubble.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
    .${ROOT_CLASS}__sprite {
      font-size: 44px;
      line-height: 1;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
      transition: transform 220ms ease-out;
    }
    .${ROOT_CLASS}__sprite.is-cheer {
      animation: mascotJump 360ms ease-out;
    }
    .${ROOT_CLASS}__sprite.is-encourage {
      animation: mascotShake 320ms ease-out;
    }
    .${ROOT_CLASS}__sprite.is-streak {
      animation: mascotBig 600ms ease-out;
    }
    @keyframes mascotJump {
      0%   { transform: translateY(0)    scale(1); }
      40%  { transform: translateY(-12px) scale(1.1); }
      100% { transform: translateY(0)    scale(1); }
    }
    @keyframes mascotShake {
      0%, 100% { transform: translateX(0); }
      25%      { transform: translateX(-4px) rotate(-4deg); }
      75%      { transform: translateX(4px)  rotate(4deg); }
    }
    @keyframes mascotBig {
      0%   { transform: scale(1) rotate(0); }
      30%  { transform: scale(1.4) rotate(-8deg); }
      60%  { transform: scale(1.25) rotate(8deg); }
      100% { transform: scale(1) rotate(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .${ROOT_CLASS}__sprite,
      .${ROOT_CLASS}__bubble {
        animation: none !important;
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}

interface MountOpts {
  /** 掛載容器;預設 document.body(確保固定定位生效) */
  container?: HTMLElement;
  /** 注入 controller — 共用同一隻 instance 給多個 lesson 用 */
  controller?: MascotController;
  /** 測試用 rng */
  rng?: () => number;
}

export interface MascotHandle {
  controller: MascotController;
  root: HTMLElement;
  destroy: () => void;
}

/**
 * 掛 mascot 到答題畫面。回傳 handle 讓呼叫端在 lesson 結束時 destroy。
 * react() 直接呼叫 handle.controller.react(combo, correct) 即可。
 */
export function mountMascot(opts: MountOpts = {}): MascotHandle {
  ensureStyleInjected();
  const host = opts.container ?? (typeof document !== 'undefined' ? document.body : null);
  if (!host) {
    // SSR / 測試裸環境 — 回傳 noop handle
    const noopController = opts.controller ?? new MascotController(opts.rng);
    return {
      controller: noopController,
      root: null as unknown as HTMLElement,
      destroy: () => noopController.destroy(),
    };
  }

  const root = document.createElement('div');
  root.className = ROOT_CLASS;
  root.setAttribute('aria-live', 'polite');
  root.setAttribute('aria-label', '修煉精靈');

  const bubble = document.createElement('div');
  bubble.className = `${ROOT_CLASS}__bubble`;

  const sprite = document.createElement('div');
  sprite.className = `${ROOT_CLASS}__sprite`;
  sprite.textContent = SPRITE.idle;
  sprite.style.color = COLOR.idle;

  root.appendChild(bubble);
  root.appendChild(sprite);
  host.appendChild(root);

  const controller = opts.controller ?? new MascotController(opts.rng);

  // 初始 idle 文案
  bubble.textContent = idleLine(opts.rng ?? Math.random);

  // QA #263 fix #5/#6: hide-after-idle. After CSS fade-out completes we set
  // is-gone (display:none) so the sprite stops blocking the keyboard / answer
  // cards. On the next react() we clear is-gone BEFORE applying state so the
  // fade-in animation has a layout to anchor to.
  // Cadence: ~700ms after entering idle (long enough for the bubble fade-out
  // transition to finish, short enough that the kid still sees the chick fade).
  const HIDE_AFTER_IDLE_MS = 700;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleHide(): void {
    if (hideTimer !== null) clearTimeout(hideTimer);
    root.classList.add('is-hidden');
    hideTimer = setTimeout(() => {
      // Re-check state — if a react() came in during the timeout, abort.
      if (controller.getState() === 'idle') {
        root.classList.add('is-gone');
      }
      hideTimer = null;
    }, HIDE_AFTER_IDLE_MS);
  }

  function cancelHide(): void {
    if (hideTimer !== null) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    root.classList.remove('is-hidden', 'is-gone');
  }

  const unsubscribe = controller.subscribe((fb: MascotFeedback) => {
    // Non-idle reaction → ensure sprite is visible BEFORE applying state.
    if (fb.state !== 'idle') cancelHide();

    sprite.textContent = SPRITE[fb.state];
    sprite.style.color = COLOR[fb.state];

    // 動畫 class — 先清再加,確保每次都 retrigger
    sprite.classList.remove('is-cheer', 'is-encourage', 'is-streak');
    // happy-dom 沒 reflow,但保留這行給瀏覽器
    void sprite.offsetWidth;
    if (fb.state === 'cheer') sprite.classList.add('is-cheer');
    else if (fb.state === 'encourage') sprite.classList.add('is-encourage');
    else if (fb.state === 'streak-celebrate') sprite.classList.add('is-streak');

    bubble.textContent = fb.line;
    bubble.classList.add('is-visible');
    // idle 反饋時淡出氣泡 + 整個 sprite,讓出答題卡 / 鍵盤空間
    if (fb.state === 'idle') {
      bubble.classList.remove('is-visible');
      scheduleHide();
    }
  });

  // 初始顯示 idle bubble 1.5s 後淡掉,再 HIDE_AFTER_IDLE_MS 後整個 sprite hide
  bubble.classList.add('is-visible');
  const initialHide = setTimeout(() => {
    bubble.classList.remove('is-visible');
    // 起手 idle → also hide sprite so first-impression on the lesson page
    // doesn't have a permanent chick blocking the answer surface.
    scheduleHide();
  }, 1500);

  // C04 Badge 解鎖儀式感 — 復用 C07 streak-celebrate animation. 監聽全域
  // `ef:badge-unlocked` 事件,把 badge 顯示成 「🎉 解鎖 {icon} {name}!」 慶祝氣泡
  // (用 combo=99 觸發 tier=10 streak-celebrate state — sprite 變 🎉 + 放大動畫).
  // 學習與遊戲分離:這條 listener 只跑在學習端 mascot,絕對不會影響戰鬥端。
  const onBadgeUnlocked = (e: Event) => {
    const detail = (e as CustomEvent).detail as { badge?: { icon?: string; name?: string } } | undefined;
    if (!detail?.badge) return;
    cancelHide();
    const fb = controller.react(99, true); // tier=10 → streak-celebrate state
    void fb;
    // Override the auto-picked line with badge-specific copy so the kid sees
    // exactly WHICH badge they just earned (not a generic streak line).
    bubble.textContent = `\u{1F389} 解鎖 ${detail.badge.icon ?? ''} ${detail.badge.name ?? '新徽章'}!`;
    bubble.classList.add('is-visible');
  };
  // C03 Monster caught — reuse the C07 streak-celebrate animation so a fresh
  // monster catch triggers the mascot cheer in addition to the dedicated
  // reveal banner. We only fire on NEW catches (isNew=true) to avoid spamming
  // when a kid is grinding duplicates for evolution material.
  const onMonsterCaught = (e: Event) => {
    const detail = (e as CustomEvent).detail as {
      monster?: { sprite?: string; name?: string };
      isNew?: boolean;
    } | undefined;
    if (!detail?.monster) return;
    if (detail.isNew === false) return; // duplicates — let the dex count UI handle it
    cancelHide();
    const fb = controller.react(99, true);
    void fb;
    bubble.textContent = `\u{2728} 抓到 ${detail.monster.sprite ?? ''} ${detail.monster.name ?? '新怪獸'}!`;
    bubble.classList.add('is-visible');
  };
  // C03 Monster evolved — same animation, different copy. Always celebrate
  // since evolution is rarer than catching.
  const onMonsterEvolved = (e: Event) => {
    const detail = (e as CustomEvent).detail as {
      toMonster?: { sprite?: string; name?: string };
    } | undefined;
    if (!detail?.toMonster) return;
    cancelHide();
    const fb = controller.react(99, true);
    void fb;
    bubble.textContent = `\u{1F31F} 進化! ${detail.toMonster.sprite ?? ''} ${detail.toMonster.name ?? ''}!`;
    bubble.classList.add('is-visible');
  };
  if (typeof window !== 'undefined') {
    try {
      window.addEventListener('ef:badge-unlocked', onBadgeUnlocked);
      window.addEventListener('ef:monster-caught', onMonsterCaught);
      window.addEventListener('ef:monster-evolved', onMonsterEvolved);
    } catch {
      // test harness without addEventListener — ignore
    }
  }

  return {
    controller,
    root,
    destroy(): void {
      clearTimeout(initialHide);
      if (hideTimer !== null) clearTimeout(hideTimer);
      unsubscribe();
      if (typeof window !== 'undefined') {
        try { window.removeEventListener('ef:badge-unlocked', onBadgeUnlocked); } catch { /* ignore */ }
        try { window.removeEventListener('ef:monster-caught', onMonsterCaught); } catch { /* ignore */ }
        try { window.removeEventListener('ef:monster-evolved', onMonsterEvolved); } catch { /* ignore */ }
      }
      controller.destroy();
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
